import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreVertical, Pencil, Plus, Trash2, UsersRound } from 'lucide-react';
import { useState } from 'react';

import type { EscritorioMembro } from '@/api/client';
import { atualizarEquipe, criarEquipe, excluirEquipe, type Equipe } from '@/api/equipes';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/EmptyState';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { corValida } from '@/lib/tags/cores';

import { EquipeDialog, type DadosDaEquipe } from './EquipeDialog';
import { Avatar } from './papeis';

interface EquipesAbaProps {
  escritorioId: string;
  equipes: Equipe[];
  membros: EscritorioMembro[];
  podeGerir: boolean;
  /** A lista não carregou: sem o convite a criar (o aviso de erro fica na página). */
  falhou?: boolean;
}

/** As equipes do escritório (R6): todo membro vê; Proprietário e Sócio criam e editam. */
export function EquipesAba({ escritorioId, equipes, membros, podeGerir, falhou = false }: EquipesAbaProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [editando, setEditando] = useState<Equipe | 'nova' | null>(null);
  const [aExcluir, setAExcluir] = useState<Equipe | null>(null);

  const recarregar = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.escritorios.equipes(escritorioId),
    });
  const salvar = useMutation({
    mutationFn: (dados: DadosDaEquipe) =>
      editando && editando !== 'nova'
        ? atualizarEquipe(escritorioId, editando.id, dados)
        : criarEquipe(escritorioId, dados),
    onSuccess: () => {
      showSuccess(editando === 'nova' ? 'Equipe criada' : 'Equipe atualizada');
      setEditando(null);
      recarregar();
    },
    onError: (err) => notifyMutationError(err, showError),
  });
  const excluir = useMutation({
    mutationFn: (equipe: Equipe) => excluirEquipe(escritorioId, equipe.id),
    onSuccess: () => {
      showSuccess('Equipe excluída');
      setAExcluir(null);
      recarregar();
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  return (
    <div className="space-y-4">
      {podeGerir && equipes.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setEditando('nova')}>
            <Plus className="mr-2 h-4 w-4" />
            Nova equipe
          </Button>
        </div>
      )}

      {falhou ? null : equipes.length === 0 ? (
        <EmptyState
          icon={<UsersRound />}
          title="Nenhuma equipe"
          description="Equipes agrupam colegas para compartilhar de uma vez — por área, cliente ou sindicato."
          action={podeGerir ? { label: 'Criar equipe', onClick: () => setEditando('nova') } : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {equipes.map((e) => (
            <Card key={e.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="h-3 w-3 shrink-0 rounded-full border"
                      style={{ backgroundColor: corValida(e.cor) }}
                    />
                    <p className="break-words font-medium">{e.nome}</p>
                  </div>
                  {podeGerir && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="-mr-2 -mt-1 h-11 w-11 shrink-0 sm:h-8 sm:w-8"
                          aria-label={`Ações da equipe ${e.nome}`}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditando(e)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setAExcluir(e)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                {e.membros.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sem membros</p>
                ) : (
                  <ul className="space-y-1.5">
                    {e.membros.map((m) => (
                      <li key={m.usuario_id} className="flex items-center gap-2 text-sm">
                        <Avatar nome={m.usuario_nome} className="h-6 w-6 text-[11px]" />
                        <span className="min-w-0 truncate">{m.usuario_nome}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <EquipeDialog
        open={editando !== null}
        onOpenChange={(aberto) => !aberto && setEditando(null)}
        equipe={editando && editando !== 'nova' ? editando : undefined}
        membros={membros}
        salvando={salvar.isPending}
        onSalvar={(dados) => salvar.mutate(dados)}
      />
      <ConfirmDialog
        open={!!aExcluir}
        title="Excluir equipe"
        message={
          <>
            O que estava compartilhado com <strong>{aExcluir?.nome}</strong> deixa de valer para os membros dela.
          </>
        }
        confirmLabel="Excluir"
        confirmColor="error"
        loading={excluir.isPending}
        onConfirm={() => aExcluir && excluir.mutate(aExcluir)}
        onCancel={() => setAExcluir(null)}
      />
    </div>
  );
}
